// ===================================================================
// 📋 GERENCIADOR DE QUESTIONÁRIOS SOCIOECONÔMICOS - Fase 1
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

export const QuestionnaireManager = React.memo(({ user }) => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    intro_text: '',
    questions: []
  });
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getQuestionnaires(true);
      setQuestionnaires(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erro ao carregar questionários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const addQuestion = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      questions: [...(prev.questions || []), { question_text: '', alternatives: [''] }]
    }));
  }, []);

  const updateQuestion = useCallback((idx, field, value) => {
    setFormData(prev => {
      const qs = [...(prev.questions || [])];
      if (!qs[idx]) return prev;
      qs[idx] = { ...qs[idx], [field]: value };
      return { ...prev, questions: qs };
    });
  }, []);

  const removeQuestion = useCallback((idx) => {
    setFormData(prev => ({
      ...prev,
      questions: (prev.questions || []).filter((_, i) => i !== idx)
    }));
  }, []);

  const addAlternative = useCallback((qIdx) => {
    setFormData(prev => {
      const qs = [...(prev.questions || [])];
      if (!qs[qIdx]) return prev;
      const alts = [...(qs[qIdx].alternatives || []), ''];
      qs[qIdx] = { ...qs[qIdx], alternatives: alts };
      return { ...prev, questions: qs };
    });
  }, []);

  const updateAlternative = useCallback((qIdx, aIdx, value) => {
    setFormData(prev => {
      const qs = [...(prev.questions || [])];
      if (!qs[qIdx]) return prev;
      const alts = [...(qs[qIdx].alternatives || [])];
      alts[aIdx] = value;
      qs[qIdx] = { ...qs[qIdx], alternatives: alts };
      return { ...prev, questions: qs };
    });
  }, []);

  const removeAlternative = useCallback((qIdx, aIdx) => {
    setFormData(prev => {
      const qs = [...(prev.questions || [])];
      if (!qs[qIdx]) return prev;
      const alts = (qs[qIdx].alternatives || []).filter((_, i) => i !== aIdx);
      qs[qIdx] = { ...qs[qIdx], alternatives: alts.length ? alts : [''] };
      return { ...prev, questions: qs };
    });
  }, []);

  const buildPayload = useCallback(() => {
    const questions = (formData.questions || [])
      .map(q => ({
        question_text: (q.question_text || '').trim(),
        alternatives: (q.alternatives || []).map(a => (typeof a === 'string' ? a : (a.alternative_text || '')).trim()).filter(Boolean)
      }))
      .filter(q => q.question_text);
    return {
      name: (formData.name || '').trim(),
      description: (formData.description || '').trim() || undefined,
      intro_text: (formData.intro_text || '').trim() || undefined,
      questions
    };
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload.name) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        const { data, error: updateError } = await apiClient.updateQuestionnaire(editingId, payload);
        if (updateError) {
          setError(updateError);
        } else {
          addNotification('Sucesso', 'Questionário atualizado', 'success');
          setShowModal(false);
          setEditingId(null);
          loadData();
        }
      } else {
        const { data, error: createError } = await apiClient.createQuestionnaire(payload);
        if (createError) {
          setError(createError);
        } else {
          addNotification('Sucesso', 'Questionário criado', 'success');
          setShowModal(false);
          loadData();
        }
      }
    } catch (err) {
      setError('Erro ao salvar');
    } finally {
      setLoading(false);
    }
  }, [formData, editingId, buildPayload, loadData, addNotification]);

  const handleEdit = useCallback(async (q) => {
    setEditingId(q.id);
    setError('');
    try {
      const data = await apiClient.getQuestionnaire(q.id);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        intro_text: data.intro_text || '',
        questions: (data.questions || []).map(qu => ({
          question_text: qu.question_text || '',
          alternatives: (qu.alternatives || []).map(a => a.alternative_text || '')
        }))
      });
      setShowModal(true);
    } catch (err) {
      setError('Erro ao carregar questionário');
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Excluir este questionário? Esta ação não pode ser desfeita.')) return;
    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteQuestionnaire(id);
      if (deleteError) {
        addNotification('Erro', deleteError, 'error');
      } else {
        addNotification('Sucesso', 'Questionário excluído', 'success');
        loadData();
      }
    } catch (err) {
      addNotification('Erro', 'Erro ao excluir', 'error');
    } finally {
      setLoading(false);
    }
  }, [loadData, addNotification]);

  const handleNew = useCallback(() => {
    setEditingId(null);
    setFormData({ name: '', description: '', intro_text: '', questions: [] });
    setError('');
    setShowModal(true);
  }, []);

  if (loading && questionnaires.length === 0) {
    return <LoadingSpinner text="Carregando questionários..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={{ ...styles.flexBetween, marginBottom: 16 }}>
          <h2 style={styles.cardTitle}>📋 Questionários Socioeconômicos</h2>
          <button onClick={handleNew} style={{ ...styles.button, ...styles.buttonPrimary }}>
            ➕ Novo Questionário
          </button>
        </div>

        {error && !showModal && (
          <Alert message={error} type="error" onClose={() => setError('')} />
        )}

        {questionnaires.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
            <h3>Nenhum questionário cadastrado</h3>
            <p>Crie questionários com perguntas e alternativas para usar nos processos seletivos</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Nome</th>
                  <th style={styles.tableHeaderCell}>Perguntas</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {questionnaires.map(q => (
                  <tr key={q.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: 600, color: '#1F2937' }}>{q.name}</span>
                      {q.description && (
                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{q.description}</div>
                      )}
                    </td>
                    <td style={styles.tableCell}>{q.questions_count ?? 0}</td>
                    <td style={styles.tableCell}>
                      <button onClick={() => handleEdit(q)} style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 8px', fontSize: 12, marginRight: 8 }}>
                        Editar
                      </button>
                      <button onClick={() => handleDelete(q.id)} style={{ ...styles.button, padding: '4px 8px', fontSize: 12, backgroundColor: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' }}>
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditingId(null); setError(''); }} title={editingId ? 'Editar Questionário' : 'Novo Questionário'}>
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <Alert message={error} type="error" onClose={() => setError('')} />}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Nome *</label>
              <input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} style={styles.input} placeholder="Ex: Questionário 2025" required />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Descrição</label>
              <input type="text" value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} style={styles.input} placeholder="Opcional" />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Texto introdutório</label>
              <textarea value={formData.intro_text} onChange={(e) => handleInputChange('intro_text', e.target.value)} style={{ ...styles.input, minHeight: 80 }} placeholder="Aviso de sigilo, instruções..." />
            </div>
          </div>

          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label style={{ ...styles.label, marginBottom: 0 }}>Perguntas</label>
              <button type="button" onClick={addQuestion} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: 13 }}>
                ➕ Adicionar pergunta
              </button>
            </div>

            {(formData.questions || []).map((q, qIdx) => (
              <div key={qIdx} style={{ marginBottom: 20, padding: 16, border: '1px solid #E5E7EB', borderRadius: 8, backgroundColor: '#F9FAFB' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <label style={{ ...styles.label, fontSize: 12 }}>Pergunta {qIdx + 1}</label>
                  <button type="button" onClick={() => removeQuestion(qIdx)} style={{ color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Remover</button>
                </div>
                <input type="text" value={q.question_text || ''} onChange={(e) => updateQuestion(qIdx, 'question_text', e.target.value)} style={styles.input} placeholder="Texto da pergunta" />
                <div style={{ marginTop: 12 }}>
                  <label style={{ ...styles.label, fontSize: 11, color: '#6B7280' }}>Alternativas</label>
                  {(q.alternatives || ['']).map((alt, aIdx) => (
                    <div key={aIdx} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <input type="text" value={typeof alt === 'string' ? alt : (alt.alternative_text || '')} onChange={(e) => updateAlternative(qIdx, aIdx, e.target.value)} style={styles.input} placeholder={`Alternativa ${aIdx + 1}`} />
                      <button type="button" onClick={() => removeAlternative(qIdx, aIdx)} style={{ color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addAlternative(qIdx)} style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 10px', fontSize: 12, marginTop: 8 }}>
                    + Alternativa
                  </button>
                </div>
              </div>
            ))}
            <button type="button" onClick={addQuestion} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: 13, marginTop: 8 }}>
              ➕ Adicionar pergunta
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button type="submit" style={{ ...styles.button, ...styles.buttonPrimary }}>Salvar</button>
            <button type="button" onClick={() => { setShowModal(false); setEditingId(null); }} style={styles.button}>Cancelar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
});
